import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  deleteField,
  writeBatch,
  limit
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { db, storage, shouldSkipFirestore } from './config'
import { getCurrentUser } from './auth'
import { logger } from '@/utils/logger'
import { errorHandler } from '@/utils/errorHandler'
import { optimizeImage } from '@/utils/imageConverter'

export interface PortfolioProject {
  id?: string
  title: string
  description: string
  screenshots: string[]
  technologies: string[]
  liveUrl?: string
  githubUrl?: string
  /**
   * Optional: Route zur detaillierten Projektseite (z.B. '/projects/m-drei')
   * 
   * WICHTIG: Nur für spezielle Projekte, die eine eigene Detailseite haben sollen!
   * Wenn gesetzt, erscheint ein "Details"-Button im PortfolioItem und
   * die gesamte Karte wird klickbar zur Projektseite.
   * 
   * Normale Projekte lassen dieses Feld leer und werden nur im Portfolio-Grid angezeigt.
   */
  projectPageUrl?: string
  // Neu: Logo-Upload (nur für einfache Projekte)
  logoLight?: string     // URL zum Logo für Light Mode
  logoDark?: string      // URL zum Logo für Dark Mode
  
  // Neu: Feature-Farbe (nur für einfache Projekte)
  accentColor?: string   // Hex-Code, z.B. "#3B82F6"
  
  // Neu: Zusätzliche Medien (nur für einfache Projekte)
  gallery?: {
    url: string         // URL zum Bild/Video
    type: 'image' | 'video'
    caption?: string    // Optionale Beschriftung
    order: number       // Sortierung
  }[]
  
  // Neu: Favorit-Status - nur favorisierte Projekte werden auf der Startseite angezeigt
  isFavorite?: boolean  // Standard: false
  favoriteOrder?: number // Reihenfolge für Favoriten
  regularOrder?: number  // Reihenfolge für reguläre Projekte
  
  createdAt?: Date
  updatedAt?: Date
}

// Portfolio-Projekte abrufen
export const getPortfolioProjects = async (): Promise<PortfolioProject[]> => {
  // Für Bots/Performance-Tools: Keine Firestore-Abfrage, um Timeouts zu vermeiden
  if (shouldSkipFirestore || !db) {
    logger.debug('Firestore übersprungen - Portfolio-Projekte werden nicht geladen');
    return [];
  }
  
  const q = query(collection(db, 'portfolio'), orderBy('createdAt', 'desc'))
  const querySnapshot = await getDocs(q)
  
  return querySnapshot.docs.map(doc => {
    const data = doc.data()
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    } as PortfolioProject
  })
}

// Portfolio-Projekt nach projectPageUrl abrufen
export const getPortfolioProjectByPageUrl = async (projectPageUrl: string): Promise<PortfolioProject | null> => {
  const projects = await getPortfolioProjects()
  return projects.find(p => p.projectPageUrl === projectPageUrl) || null
}

// Portfolio-Projekt erstellen
export const createPortfolioProject = async (project: Omit<PortfolioProject, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Entferne undefined Werte, da Firestore diese nicht erlaubt
  const projectData: Omit<Partial<PortfolioProject>, 'createdAt' | 'updatedAt'> & { createdAt: Timestamp; updatedAt: Timestamp } = {
    title: project.title,
    description: project.description,
    screenshots: project.screenshots,
    technologies: project.technologies,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  }
  
  // Füge optionale Felder nur hinzu, wenn sie definiert sind
  if (project.liveUrl) {
    projectData.liveUrl = project.liveUrl
  }
  if (project.githubUrl) {
    projectData.githubUrl = project.githubUrl
  }
  if (project.projectPageUrl) {
    projectData.projectPageUrl = project.projectPageUrl
  }
  if (project.logoLight) {
    projectData.logoLight = project.logoLight
  }
  if (project.logoDark) {
    projectData.logoDark = project.logoDark
  }
  if (project.accentColor) {
    projectData.accentColor = project.accentColor
  }
  if (project.gallery) {
    projectData.gallery = project.gallery
  }
  if (project.isFavorite !== undefined) {
    projectData.isFavorite = project.isFavorite
  }
  if (project.favoriteOrder !== undefined) {
    projectData.favoriteOrder = project.favoriteOrder
  }
  if (project.regularOrder !== undefined) {
    projectData.regularOrder = project.regularOrder
  }
  
  const docRef = await addDoc(collection(db, 'portfolio'), projectData)
  return docRef.id
}

// Portfolio-Projekt aktualisieren
export const updatePortfolioProject = async (id: string, project: Partial<PortfolioProject>): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const docRef = doc(db, 'portfolio', id)
  
  // Entferne undefined Werte, da Firestore diese nicht erlaubt
  const updateData: Record<string, any> = {
    updatedAt: Timestamp.now()
  }
  
  // Füge nur definierte Felder hinzu
  if (project.title !== undefined) updateData.title = project.title
  if (project.description !== undefined) updateData.description = project.description
  if (project.screenshots !== undefined) updateData.screenshots = project.screenshots
  if (project.technologies !== undefined) updateData.technologies = project.technologies
  if (project.liveUrl !== undefined) {
    if (project.liveUrl && project.liveUrl.trim()) {
      updateData.liveUrl = project.liveUrl.trim()
    } else {
      // Wenn leerer String oder undefined, entferne das Feld
      updateData.liveUrl = deleteField()
    }
  }
  if (project.githubUrl !== undefined) {
    if (project.githubUrl && project.githubUrl.trim()) {
      updateData.githubUrl = project.githubUrl.trim()
    } else {
      // Wenn leerer String oder undefined, entferne das Feld
      updateData.githubUrl = deleteField()
    }
  }
  if (project.projectPageUrl !== undefined) {
    if (project.projectPageUrl && project.projectPageUrl.trim()) {
      updateData.projectPageUrl = project.projectPageUrl.trim()
    } else {
      // Wenn leerer String oder undefined, entferne das Feld
      updateData.projectPageUrl = deleteField()
    }
  }
  if (project.logoLight !== undefined) {
    updateData.logoLight = project.logoLight ? project.logoLight : deleteField()
  }
  if (project.logoDark !== undefined) {
    updateData.logoDark = project.logoDark ? project.logoDark : deleteField()
  }
  if (project.accentColor !== undefined) {
    updateData.accentColor = project.accentColor ? project.accentColor : deleteField()
  }
  if (project.gallery !== undefined) {
    updateData.gallery = project.gallery ? project.gallery : deleteField()
  }
  if (project.isFavorite !== undefined) {
    updateData.isFavorite = project.isFavorite
  }
  if (project.favoriteOrder !== undefined) {
    updateData.favoriteOrder = project.favoriteOrder
  }
  if (project.regularOrder !== undefined) {
    updateData.regularOrder = project.regularOrder
  }
  
  await updateDoc(docRef, updateData)
}

// Portfolio-Projekt löschen
export const deletePortfolioProject = async (id: string, screenshotUrls: string[], logoLight?: string, logoDark?: string, galleryUrls?: string[]): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Screenshots aus Storage löschen
  const allUrls = [...screenshotUrls]
  if (logoLight) allUrls.push(logoLight)
  if (logoDark) allUrls.push(logoDark)
  if (galleryUrls) allUrls.push(...galleryUrls)

  for (const url of allUrls) {
    try {
      // Extrahiere den Pfad aus der Download-URL
      // Format: https://firebasestorage.googleapis.com/v0/b/BUCKET/o/PATH?alt=media&token=TOKEN
      const urlObj = new URL(url)
      const pathMatch = urlObj.pathname.match(/\/o\/(.+)/)
      if (pathMatch) {
        const decodedPath = decodeURIComponent(pathMatch[1])
        const imageRef = ref(storage, decodedPath)
        await deleteObject(imageRef)
      }
    } catch (error) {
      logger.error('Fehler beim Löschen der Datei aus Storage', error)
    }
  }
  
  // Dokument aus Firestore löschen
  const docRef = doc(db, 'portfolio', id)
  await deleteDoc(docRef)
}

// Logo hochladen
export const uploadProjectLogo = async (file: File, projectId: string, mode: 'light' | 'dark'): Promise<string> => {
  const user = getCurrentUser()
  if (!user) throw new Error('Nicht authentifiziert')
  await user.getIdToken(true)

  // Konvertiere Bild zu WebP für bessere Kompression (nur bei Bildern)
  let optimizedFile: File = file
  if (file.type.startsWith('image/')) {
    try {
      optimizedFile = await optimizeImage(file)
      logger.info(`Logo optimiert: ${file.size} bytes → ${optimizedFile.size} bytes`)
    } catch (error) {
      logger.warn('Fehler bei Logo-Optimierung, verwende Originaldatei', error)
    }
  }

  const fileName = `portfolio/${projectId}/logo-${mode}-${Date.now()}.webp`
  const storageRef = ref(storage, fileName)
  
  await uploadBytes(storageRef, optimizedFile)
  return await getDownloadURL(storageRef)
}

// Galerie-Medium hochladen
export const uploadGalleryMedia = async (file: File, projectId: string, index: number): Promise<string> => {
  const user = getCurrentUser()
  if (!user) throw new Error('Nicht authentifiziert')
  await user.getIdToken(true)

  // Konvertiere nur Bilder zu WebP, Videos bleiben unverändert
  let optimizedFile: File = file
  let fileExtension = file.name.split('.').pop()?.toLowerCase() || 'bin'
  
  if (file.type.startsWith('image/')) {
    try {
      optimizedFile = await optimizeImage(file)
      fileExtension = 'webp'
      logger.info(`Galerie-Bild optimiert: ${file.size} bytes → ${optimizedFile.size} bytes`)
    } catch (error) {
      logger.warn('Fehler bei Galerie-Bild-Optimierung, verwende Originaldatei', error)
    }
  }

  const fileName = `portfolio/${projectId}/gallery-${index}-${Date.now()}.${fileExtension}`
  const storageRef = ref(storage, fileName)
  
  await uploadBytes(storageRef, optimizedFile)
  return await getDownloadURL(storageRef)
}

// Screenshot hochladen
export const uploadScreenshot = async (file: File, projectId: string, index: number): Promise<string> => {
  // Prüfe, ob der Benutzer authentifiziert ist und hole ein frisches Token
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }
  
  // Stelle sicher, dass wir ein frisches Token haben
  try {
    await user.getIdToken(true) // true = force refresh
  } catch (error) {
    logger.error('Fehler beim Aktualisieren des Auth-Tokens', error)
    throw new Error('Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.')
  }
  
  // Konvertiere Screenshot zu WebP für bessere Kompression
  let optimizedFile: File
  try {
    optimizedFile = await optimizeImage(file)
    logger.info(`Screenshot optimiert: ${file.size} bytes → ${optimizedFile.size} bytes`)
  } catch (error) {
    logger.warn('Fehler bei Screenshot-Optimierung, verwende Originaldatei', error)
    optimizedFile = file
  }
  
  const fileName = `portfolio/${projectId}/screenshot-${index}-${Date.now()}.webp`
  const storageRef = ref(storage, fileName)
  
  await uploadBytes(storageRef, optimizedFile)
  const downloadURL = await getDownloadURL(storageRef)
  
  return downloadURL
}

// Screenshot löschen
export const deleteScreenshot = async (url: string): Promise<void> => {
  try {
    // Extrahiere den Pfad aus der Download-URL
    const urlObj = new URL(url)
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)/)
    if (pathMatch) {
      const decodedPath = decodeURIComponent(pathMatch[1])
      const imageRef = ref(storage, decodedPath)
      await deleteObject(imageRef)
    }
  } catch (error) {
    errorHandler.handle(error, 'deleteScreenshot')
    throw error
  }
}

// Batch-Update für Order-Änderungen
export const reorderProjects = async (
  updates: { id: string; favoriteOrder?: number; regularOrder?: number }[]
): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  // Lokale Variable für TypeScript-Narrowing
  const firestore = db
  const batch = writeBatch(firestore)
  
  updates.forEach(update => {
    const docRef = doc(firestore, 'portfolio', update.id)
    const updateData: Record<string, any> = { updatedAt: Timestamp.now() }
    
    if (update.favoriteOrder !== undefined) updateData.favoriteOrder = update.favoriteOrder
    if (update.regularOrder !== undefined) updateData.regularOrder = update.regularOrder
    
    batch.update(docRef, updateData)
  })
  
  await batch.commit()
}

// Schnell-Toggle für Favorit-Status
export const toggleFavoriteStatus = async (
  id: string, 
  isFavorite: boolean
): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const docRef = doc(db, 'portfolio', id)
  const updateData: Record<string, any> = { 
    isFavorite,
    updatedAt: Timestamp.now()
  }
  
  // Wenn es zum Favoriten wird, am Ende einfügen
  if (isFavorite) {
    updateData.favoriteOrder = await getNextOrderNumber(true)
  } else {
    updateData.regularOrder = await getNextOrderNumber(false)
  }
  
  await updateDoc(docRef, updateData)
}

// Hilfsfunktion: Nächste verfügbare Order-Nummer
export const getNextOrderNumber = async (
  isFavorite: boolean
): Promise<number> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const orderField = isFavorite ? 'favoriteOrder' : 'regularOrder'
  const q = query(
    collection(db, 'portfolio'), 
    orderBy(orderField, 'desc'),
    limit(1)
  )
  
  const querySnapshot = await getDocs(q)
  if (querySnapshot.empty) return 1
  
  const lastProject = querySnapshot.docs[0].data()
  return (lastProject[orderField] || 0) + 1
}

